import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
import Clock from 'iconsax-react/dist/esm/Clock';
import { TDR } from 'tdr-common';
import { getDisplayTime } from '../../helpers/getDisplayTime';
import { AvailabilityGuardContext, SearchFilters, serializeSearchFiltersForURL } from '../../routes/AvailabilityGuard';
import { AnnouncementResponse } from '../announcements/common';
import { CardShell } from './CardShell';
import { TableCategoryCounts } from './categorizeTables';
import { emitTableClicked } from './cardAnalytics';
import { analytics } from '../../helpers/analytics';
import { RESULT_CLICKED } from '../../helpers/analytics/constants';

type CrossSellTimeCardProps = {
    restaurant: TDR.Restaurant;
    table: TDR.Table;
    searchFilters: SearchFilters;
    announcement?: AnnouncementResponse;
    viewedTable?: boolean;
    availableTimes: string[];
    counts: TableCategoryCounts;
    totalResults: number;
    position: number;
    hasScrolledToBottom?: boolean;
    timeslotData?: TDR.TimeSlotMap;
    /**
     * Set when this card sits in a service-scoped section. Its presence — not its value — is what
     * matters here: it means the card represents one package, so the click must land on the time
     * the card advertises. Without it two sibling service sections would send the guest to the
     * same timeless URL and the package they picked would be lost.
     */
    slotTime?: string;
};

function getNextAvailableTime(selectedTime: string, availableTimes: string[]): string {
    const next = availableTimes
        .filter(time => time > selectedTime)
        .sort((a, b) => a.localeCompare(b));
    return next.length ? next[0] : (availableTimes[0] ?? '');
}

const Footer = ({ nextTime, onClick }: { nextTime: string; onClick: () => void }) => {
    const { t } = useTranslation();
    return (
        <div className="bg-tablz-med-gray-1 flex items-center justify-between p-3 rounded-2xl text-sm">
            <div className="flex gap-1 items-center">
                <Clock size={16} color="#FACC15" variant="Bold" />
                <p className="font-poppins text-yellow-400">
                    <Trans
                        i18nKey="inventory.cross_sell_time_available"
                        values={{ time: getDisplayTime(nextTime) }}
                        components={[<span key="b" className="font-semibold" />]}
                    />
                </p>
            </div>
            <button
                type="button"
                className="font-poppins font-semibold text-[color:var(--zinc-200,#e4e4e7)] cursor-pointer"
                onClick={onClick}
                data-testid="see-details-btn"
            >
                {t('inventory.see_details')}
            </button>
        </div>
    );
};

export const CrossSellTimeCard = ({
    restaurant, table, searchFilters, announcement, viewedTable,
    availableTimes, counts, totalResults, position, hasScrolledToBottom, timeslotData, slotTime
}: CrossSellTimeCardProps) => {
    const navigate = useNavigate();
    const { setPreviousTime } = useOutletContext<AvailabilityGuardContext>();
    const nextTime = getNextAvailableTime(searchFilters.time || '', availableTimes);

    const handleClick = () => {
        setPreviousTime(searchFilters.time || '');
        analytics.track(RESULT_CLICKED, { resultType: 'table time cross sell' });
        emitTableClicked({
            table, restaurantId: restaurant.id, searchFilters, counts, totalResults,
            position, tableStatus: 'time_conflict', announcement, hasScrolledToBottom
        });
        navigate({
            pathname: table.slug,
            // Land on the time the footer advertises, not the searched one and not the section's
            // earliest — those diverge whenever the next start is later than the earliest.
            search: serializeSearchFiltersForURL({
                ...searchFilters,
                time: slotTime ? nextTime : undefined
            }).toString()
        });
    };

    return (
        <CardShell
            restaurant={restaurant}
            table={table}
            searchFilters={searchFilters}
            announcement={announcement}
            viewedTable={viewedTable}
            onCardClick={handleClick}
            timeslotData={timeslotData}
            footer={<Footer nextTime={nextTime} onClick={handleClick} />}
        />
    );
};
